import { ChangeDetectionStrategy, Component, effect, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { APP_ROUTES } from '@core/routes';
import { TranslationService } from '@core/translation.service';
import { AuthService } from '@features/auth/services/auth.service';
import { isAuthenticated, userAccount } from '@features/auth/store/auth.signals';
import { CustomerDropdownComponent } from '@features/customer/components/customer-dropdown/customer-dropdown.component';
import { WorkWithCustomerStore } from '@features/customer/store/work-with-customer.store';
import { NotificationBellComponent } from '@features/notifications/components/notification-bell/notification-bell.component';
import { NotificationsSignalRService } from '@features/notifications/services/notifications-signalr.service';
import { NotificationsService } from '@features/notifications/services/notifications.service';
import { allBadge } from '@features/notifications/store/services/notification.store';
import { TranslateModule } from '@ngx-translate/core';
import { PnycDropdownDirective } from '@ui-kit/directives/pnyc-dropdown/pnyc-dropdown.directive';
import { PnycTooltipDirective } from '@ui-kit/directives/pnyc-tooltip/pnyc-tooltip.directive';
import { PnycWipDirective } from '@ui-kit/directives/pnyc-wip/pnyc-wip.directive';
import { PnycAvatarComponent } from '@ui-kit/pnyc-avatar/pnyc-avatar.component';
import { PnycButtonComponent } from '@ui-kit/pnyc-button/pnyc-button.component';
import { PnycIconComponent } from '@ui-kit/pnyc-icon/pnyc-icon.component';
import { PnycListItemComponent } from '@ui-kit/pnyc-list-item/pnyc-list-item.component';
import { filter } from 'rxjs';
import { LogoComponent } from '../logo/logo.component';
import { locale } from './i18n/en';
import { ToolbarService } from './services/toolbar.service';

@Component({
    selector: 'toolbar',
    templateUrl: './toolbar.component.html',
    styleUrls: ['./toolbar.component.scss'],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        LogoComponent,
        PnycButtonComponent,
        PnycListItemComponent,
        PnycDropdownDirective,
        PnycTooltipDirective,
        PnycWipDirective,
        PnycIconComponent,
        PnycAvatarComponent,
        CustomerDropdownComponent,
        NotificationBellComponent,
        TranslateModule
    ]
})
export class ToolbarComponent {
    public readonly APP_ROUTES = APP_ROUTES;

    public readonly customerName = signal('');

    public readonly isUserAuthorized = isAuthenticated;
    public readonly userAccount = userAccount;
    public readonly notificationBadge = allBadge;

    auth = inject(AuthService);
    router = inject(Router);
    toolbarService = inject(ToolbarService);
    translationLoaderService = inject(TranslationService);
    workWithCustomerStore = inject(WorkWithCustomerStore);
    private readonly notificationsService = inject(NotificationsService);
    private readonly notificationsSignalRService = inject(NotificationsSignalRService);

    public readonly userItems = this.toolbarService.userMenuItems;
    public readonly items = this.toolbarService.toolbarItems;

    searchSelected = signal(false);
    agentHubSelected = signal(false);

    private readonly routerEventsNavigationEndSignal = toSignal(
        this.router.events.pipe(
            filter((event): event is NavigationEnd => event instanceof NavigationEnd)
        ),
        {
            initialValue: null
        }
    );

    private readonly navigationEffect = effect(() => {
        const event = this.routerEventsNavigationEndSignal();

        if (event === null) {
            return;
        }

        this.searchSelected.set(event.url.startsWith('/' + APP_ROUTES.SEARCH));
        this.agentHubSelected.set(event.url.startsWith('/' + APP_ROUTES.AGENT_HUB));
    });

    constructor() {
        this.translationLoaderService.loadTranslations(locale);
        this.workWithCustomerStore.loadFromStorage();

        effect(() => {
            if (isAuthenticated()) {
                this.notificationsService.loadUnviewedNotificationsCount();
                void this.notificationsSignalRService.connect();
            } else {
                this.notificationsSignalRService.disconnect();
            }
        });
    }

    public onLogoClicked(): void {
        this.router.navigate(['/']);
    }

    public async onLogoutClicked(): Promise<void> {
        await this.auth.signOut();
        this.router.navigate(['/']);
    }
}
